import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

import { acceptAgreementInvite, getAgreementInvite } from "@/lib/agreements.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export const Route = createFileRoute("/accept/$token")({
  component: AcceptPage,
  head: () => ({
    meta: [
      { title: "accept agreement — what" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function AcceptPage() {
  const { token } = Route.useParams();
  const router = useRouter();
  const getInvite = useServerFn(getAgreementInvite);
  const acceptInvite = useServerFn(acceptAgreementInvite);

  const [signatory, setSignatory] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["agreement-invite", token],
    queryFn: () => getInvite({ data: { token } }),
    retry: false,
  });

  if (isLoading) {
    return (
      <Centered>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </Centered>
    );
  }

  if (isError || !data) {
    return (
      <StatusCard
        tone="error"
        title="something went wrong"
        body="we couldn't load this invitation. please check the link or contact us."
      />
    );
  }

  if (data.status === "invalid") {
    return (
      <StatusCard
        tone="error"
        title="invalid invitation"
        body="this link is not recognised. please check that you've used the link from your email."
      />
    );
  }
  if (data.status === "revoked") {
    return (
      <StatusCard
        tone="error"
        title="invitation revoked"
        body="this invitation has been revoked. please contact us to request a new one."
      />
    );
  }
  if (data.status === "expired") {
    return (
      <StatusCard
        tone="error"
        title="invitation expired"
        body="this invitation has expired. please contact us to request a fresh link."
      />
    );
  }

  if (data.status === "accepted" || success) {
    const acceptedAt =
      data.status === "accepted" ? data.invite.acceptedAt : new Date().toISOString();
    return (
      <StatusCard
        tone="success"
        title="agreement accepted"
        body={`thank you. acceptance was recorded on ${new Date(acceptedAt).toUTCString()}. a confirmation has been sent to your inbox.`}
        cta={{ label: "continue to onboarding", to: "/onboarding" }}
      />
    );
  }

  const invite = data.invite;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!confirmed) {
      setError("please confirm you have read the agreement.");
      return;
    }
    if (!signatory.trim()) {
      setError("please enter your full name as signatory.");
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvite({
        data: { token, signatoryName: signatory.trim(), confirmedRead: true },
      });
      setSuccess(true);
      // Refetch to reflect server state.
      router.invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : "something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <ShieldCheck className="h-4 w-4" />
          secure agreement acceptance — what finland oy
        </div>

        <h1 className="text-2xl sm:text-3xl font-semibold mb-2">partnership agreement</h1>
        <p className="text-muted-foreground mb-6">
          for <span className="font-medium text-foreground">{invite.businessName}</span> ·
          version {invite.agreementVersion} · expires{" "}
          {new Date(invite.expiresAt).toLocaleDateString()}
        </p>

        <div className="border rounded-lg p-5 bg-muted/30 max-h-[55vh] overflow-y-auto mb-6">
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
            {invite.agreementBody}
          </pre>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="signatory" className="block text-sm font-medium mb-1.5">
              your full name (signatory)
            </label>
            <Input
              id="signatory"
              value={signatory}
              onChange={(e) => setSignatory(e.target.value)}
              placeholder="e.g. matti meikäläinen"
              maxLength={200}
              required
              disabled={submitting}
            />
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(v === true)}
              disabled={submitting}
              className="mt-0.5"
            />
            <span className="text-sm leading-relaxed">
              i confirm i have read and accept the agreement above on behalf of{" "}
              <span className="font-medium">{invite.businessName}</span>, and that i am
              authorised to do so.
            </span>
          </label>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting || !confirmed || !signatory.trim()}
            className="w-full sm:w-auto"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            accept agreement
          </Button>

          <p className="text-xs text-muted-foreground pt-2">
            by clicking accept, your ip address, timestamp, and browser will be recorded as
            proof of consent.
          </p>
        </form>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">{children}</div>
  );
}

function StatusCard({
  tone,
  title,
  body,
  cta,
}: {
  tone: "success" | "error";
  title: string;
  body: string;
  cta?: { label: string; to: string };
}) {
  const Icon = tone === "success" ? CheckCircle2 : XCircle;
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-background">
      <div className="max-w-md w-full border rounded-xl p-8 text-center">
        <Icon
          className={`h-12 w-12 mx-auto mb-4 ${
            tone === "success" ? "text-emerald-600" : "text-destructive"
          }`}
        />
        <h1 className="text-xl font-semibold mb-2">{title}</h1>
        <p className="text-sm text-muted-foreground">{body}</p>
        {cta && (
          <a
            href={cta.to}
            className="inline-flex items-center justify-center mt-6 px-5 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition"
          >
            {cta.label}
          </a>
        )}
      </div>
    </div>
  );
}
